import { useEffect, useRef } from "react";
import {
  format,
  formatDistanceToNowStrict,
  isSameYear,
  isThisMonth,
  isThisWeek,
  isToday,
  isYesterday,
} from "date-fns";
import { ChevronDoubleLeftIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useCustomerConversations } from "@api/intelligenceController";
import { cn } from "@utils";

// A plain ring spinner (the project's Tailwind overrides @keyframes spin, so `animate-spin` doesn't rotate;
// doc-spin in index.css is a real rotation).
function Spinner({ className }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cn(
        "doc-spin inline-block rounded-full border-2 border-current border-r-transparent",
        className,
      )}
    />
  );
}

// The first thing the user typed in the live thread — the preview for a freshly-started conversation that
// hasn't been persisted into the fetched list yet.
function liveThreadPreview(messages) {
  const firstUser = (messages ?? []).find((m) => m.role === "user");
  return firstUser?.text?.trim() || null;
}

// Coarse bucket for the section header. Most-specific first — this week includes today, this month
// includes this week — so a row lands in the tightest bucket that fits. Week starts Monday (en-GB).
function groupLabel(updatedAt) {
  const date = new Date(updatedAt);
  if (isToday(date)) return "Today";
  if (isThisWeek(date, { weekStartsOn: 1 })) return "This week";
  if (isThisMonth(date)) return "This month";
  return "Earlier";
}

// The per-row timestamp. Relative while it's still legible (today), then named days within the week,
// then an absolute date — so an older thread reads "12 Oct", never "19 days ago".
function timeLabel(updatedAt) {
  const date = new Date(updatedAt);
  if (isToday(date)) {
    return Date.now() - date.getTime() < 60_000
      ? "Just now"
      : formatDistanceToNowStrict(date, { addSuffix: true });
  }
  if (isYesterday(date)) return "Yesterday";
  if (isThisWeek(date, { weekStartsOn: 1 })) return format(date, "EEEE");
  return isSameYear(date, new Date()) ? format(date, "d MMM") : format(date, "d MMM yyyy");
}

function ConversationRow({ conversation, isActive, isLoading, onSelect }) {
  const preview = conversation.preview?.trim() || "New conversation";
  const count = conversation.suggestionCount;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={isActive ? "true" : undefined}
      className={cn(
        "flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left transition-colors hover:bg-zinc-100/70",
        isActive && "bg-zinc-50",
      )}
    >
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "block truncate text-sm font-medium",
            isActive ? "text-zinc-900" : "text-zinc-800",
          )}
        >
          {preview}
        </span>
        <span className="mt-0.5 block text-xs text-zinc-500">
          {timeLabel(conversation.updatedAt)}
          {count > 0 && ` · ${count} suggestion${count === 1 ? "" : "s"}`}
        </span>
      </span>
      {isLoading && <Spinner className="mt-0.5 size-4 shrink-0 text-zinc-400" />}
    </button>
  );
}

// Conversation-history rail: the signed-in user's past Smart Note threads with this customer, newest first.
// Clicking one reopens it (read-only transcript, resumable by typing). "New conversation" starts a fresh
// thread. A freshly-started thread that the server hasn't listed yet is shown as an active row up top so the
// rail always reflects where the user is.
export default function HistorySidebar({
  open = true,
  customerId,
  activeConversationId,
  loadingConversationId,
  liveMessages,
  onSelectConversation,
  onNewConversation,
  onCollapse,
}) {
  // Only fetch while the drawer is open — it defaults closed, so a wasted fetch on every modal open is
  // avoided; react-query keeps the list cached for the close animation.
  const { data: conversations = [], isLoading } = useCustomerConversations(customerId, {
    enabled: open,
  });

  // A reopened past thread means the user is viewing history (that row stays highlighted). Otherwise
  // they're in the live chat, which always gets a pinned, selected entry at the top — like ChatGPT's
  // current chat — so something is always selected, even before the first message.
  const reopened = (liveMessages ?? []).some((m) => m.historical);
  const showCurrentEntry = !reopened;
  const currentPreview = liveThreadPreview(liveMessages);

  // Once the live thread has been persisted it also comes back in the fetched list; drop it there so it
  // isn't shown twice — the pinned "Current" entry is its home while it's the active one.
  const pastConversations = showCurrentEntry
    ? conversations.filter((c) => c.conversationId !== activeConversationId)
    : conversations;

  const groups = [];
  for (const conversation of pastConversations) {
    const label = groupLabel(conversation.updatedAt);
    const group = groups.find((g) => g.label === label);
    if (group) group.items.push(conversation);
    else groups.push({ label, items: [conversation] });
  }

  // When a reopened old chat gets its FIRST new message it jumps to the top of the list (optimistic bump);
  // scroll the rail up so the user sees it land there. Fires once per reopened thread — on the transition
  // to "has a live message", not on every subsequent turn — and re-arms when another old chat is reopened.
  const listRef = useRef(null);
  const continuedRef = useRef(false);
  const continuingReopened = reopened && (liveMessages ?? []).some((m) => !m.historical);
  useEffect(() => {
    if (continuingReopened && !continuedRef.current) {
      listRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }
    continuedRef.current = continuingReopened;
  }, [continuingReopened]);

  return (
    <div className="flex h-full w-64 shrink-0 flex-col border-r border-zinc-200 bg-white">
      <div className="flex items-center justify-between px-4 py-4">
        <span className="text-xs font-semibold uppercase tracking-wide text-zinc-400">History</span>
        <button
          type="button"
          onClick={onCollapse}
          aria-label="Hide history"
          className="rounded-md p-1 text-zinc-600 transition-colors hover:bg-zinc-200 hover:text-zinc-800"
        >
          <ChevronDoubleLeftIcon aria-hidden="true" className="size-4" />
        </button>
      </div>

      <div className="px-3">
        <button
          type="button"
          onClick={onNewConversation}
          className="flex w-full items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 shadow-sm transition-colors hover:border-zinc-300 hover:text-zinc-900"
        >
          <PlusIcon aria-hidden="true" className="size-4" />
          New conversation
        </button>
      </div>

      <div ref={listRef} className="mt-4 min-h-0 flex-1 overflow-y-auto px-3 pb-4">
        {showCurrentEntry && (
          <div className="mb-4">
            <p className="px-1 pb-1 text-xs font-medium uppercase tracking-wide text-zinc-400">
              Current
            </p>
            <div
              aria-current="true"
              className="rounded-lg bg-zinc-50 px-3 py-2 transition-colors hover:bg-zinc-100/70"
            >
              <p className="truncate text-sm font-medium text-zinc-900">
                {currentPreview || "New conversation"}
              </p>
              <p className="mt-0.5 text-xs text-zinc-500">
                {currentPreview ? "In progress" : "Not started yet"}
              </p>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center gap-2 px-1 py-2 text-xs text-zinc-400">
            <Spinner className="size-4 text-zinc-300" />
            Loading conversations…
          </div>
        ) : groups.length === 0 ? (
          <p className="px-1 pt-2 text-xs text-zinc-400">
            {showCurrentEntry
              ? "Past conversations will appear here as you have them."
              : "Past conversations with this account will appear here."}
          </p>
        ) : (
          groups.map((group) => (
            <div key={group.label} className="mb-4">
              <p className="px-1 pb-1 text-xs font-medium uppercase tracking-wide text-zinc-400">
                {group.label}
              </p>
              <div className="flex flex-col gap-0.5">
                {group.items.map((conversation) => (
                  <ConversationRow
                    key={conversation.conversationId}
                    conversation={conversation}
                    isActive={conversation.conversationId === activeConversationId}
                    isLoading={conversation.conversationId === loadingConversationId}
                    onSelect={() => onSelectConversation(conversation.conversationId)}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
